import { create } from 'zustand';

interface HighlightInfo {
  fields: string[];
  timestamp: number;
  updatedBy?: string;
}

interface InspectionHighlightState {
  // findingId -> highlight info
  highlightedFindings: Record<string, HighlightInfo>;

  // 하이라이트 추가
  addHighlight: (findingId: string, fields: string[], updatedBy?: string) => void;

  // 하이라이트 제거
  removeHighlight: (findingId: string) => void;

  // 특정 필드 하이라이트만 제거
  removeFieldHighlight: (findingId: string, field: string) => void;

  // 모든 하이라이트 제거
  clearHighlights: () => void;

  // 특정 finding이 하이라이트되어 있는지 확인
  isHighlighted: (findingId: string) => boolean;

  // 특정 필드가 하이라이트되어 있는지 확인
  isFieldHighlighted: (findingId: string, field: string) => boolean;

  // 특정 finding의 하이라이트 정보 가져오기
  getHighlightInfo: (findingId: string) => HighlightInfo | undefined;
}

export const useInspectionHighlightStore = create<InspectionHighlightState>((set, get) => ({
  highlightedFindings: {},

  addHighlight: (findingId, fields, updatedBy) => {
    const timestamp = Date.now();
    const existing = get().highlightedFindings[findingId];

    // 기존 하이라이트가 있으면 필드 병합
    const mergedFields = existing
      ? [...new Set([...existing.fields, ...fields])]
      : fields;

    set((state) => ({
      highlightedFindings: {
        ...state.highlightedFindings,
        [findingId]: { fields: mergedFields, timestamp, updatedBy },
      },
    }));
  },

  removeHighlight: (findingId) => {
    set((state) => {
      const { [findingId]: _removed, ...rest } = state.highlightedFindings;
      void _removed; // ESLint: unused variable
      return { highlightedFindings: rest };
    });
  },

  removeFieldHighlight: (findingId, field) => {
    const current = get().highlightedFindings[findingId];
    if (!current) return;

    const remainingFields = current.fields.filter(f => f !== field);

    if (remainingFields.length === 0) {
      // 모든 필드가 제거되면 전체 하이라이트 제거
      get().removeHighlight(findingId);
    } else {
      set((state) => ({
        highlightedFindings: {
          ...state.highlightedFindings,
          [findingId]: { ...current, fields: remainingFields },
        },
      }));
    }
  },

  clearHighlights: () => {
    set({ highlightedFindings: {} });
  },

  isHighlighted: (findingId) => {
    return !!get().highlightedFindings[findingId];
  },

  isFieldHighlighted: (findingId, field) => {
    const info = get().highlightedFindings[findingId];
    if (!info) return false;
    // 필드 이름 또는 상위 필드 (예: riskAssessment.likelihood -> riskAssessment)
    return info.fields.some(f => f === field || f.startsWith(`${field}.`) || field.startsWith(`${f}.`));
  },

  getHighlightInfo: (findingId) => {
    return get().highlightedFindings[findingId];
  },
}));
